import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kJsonObject } from "../../../Admin/kReusableCoreTS/kJsonObject";
import { AppDemo } from "./AppDemo";
import { AppDropboxMedia } from "./AppDropboxSource";
import { AppMediaRules } from "./AppMediaRules";
import { AppLocalSource, AppSendState, AppTextDone, AppTextFail } from "./AppSource";

// AppDemoSource - DEMO MODE's archive (`AppDemo`): the service's INVENTED folder, read file by file from
// `GET /demo?path=<folder-relative path>` (`Bridge/src/DemoRun.ts`). The local source's reader does the reads - the
// folder simply lives behind that address instead of beside the page.
// ⛔ NOTHING IS SENT: `Queue` writes nothing anywhere and answers as if queued; `Check` then says "sent" so the bubble
// behaves as it would. The service also refuses every demo number as a target (`DemoNumber`, `Outbox.Admits`).

export class AppDemoSource extends AppLocalSource {
    private DemoRoot: string;
    private Base: string;
    private Number: string;

    /** `<service>/demo?set=<language set>&path=` - the folder-relative path is appended to it. */
    public static RootOf = (ServiceUrl: string): string => {
        // ERROR-SUPPRESSED-JUSTIFIED: pure string building; `AppDemo.Read` answers "" on any failure.
        return ServiceUrl.replace(/\/+$/, "") + "/demo?set=" + encodeURIComponent(AppDemo.Read(AppDemo.LangKey)) + "&path=";
    }

    public constructor(ServiceUrl: string, Number: string = "") {
        super(AppDemoSource.RootOf(ServiceUrl));
        this.Base = ServiceUrl.replace(/\/+$/, "");
        this.DemoRoot = AppDemoSource.RootOf(ServiceUrl);
        this.Number = Number;
    }

    /**
     * A picture or document of the demo folder as a `blob:` URL - the app's policy allows `img-src blob:` but not the
     * service's origin, so the bytes are read (`connect-src` allows the service) and handed over the way Dropbox's are.
     */
    public LoadMedia = (Href: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Req: XMLHttpRequest = new XMLHttpRequest();
            const Reader: AppDropboxMedia = new AppDropboxMedia(Req, AppMediaRules.TypeFor(Href), OnDone, OnFail);
            Req.onreadystatechange = Reader.OnState;
            Req.open("GET", this.DemoRoot + Href, true);
            Req.responseType = "arraybuffer";
            Req.send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemoSource.LoadMedia");
            OnFail("That picture could not be read.");
        }
    };

    /** A person file beside a page (`ai-chat.json`): its text, `OnMissing` on a 404 (the contact has none). */
    public LoadBeside = (Href: string, OnDone: AppTextDone, OnMissing: () => void, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Req: XMLHttpRequest = new XMLHttpRequest();
            Req.onreadystatechange = new AppDemoBeside(Req, OnDone, OnMissing, OnFail).OnState;
            Req.open("GET", this.DemoRoot + Href, true);
            Req.send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemoSource.LoadBeside");
            OnFail("That file could not be read.");
        }
    };

    /** ⛔ Nothing leaves the browser - the request is answered as queued, then "sent" by `Check`. */
    public Queue = (Name: string, Json: string, OnDone: AppTextDone, OnFail: AppTextFail): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⭐ The invented chat answers (`/demo/reply` -> `DemoLive`): the words go into the DEMO journal only, the contact
            // "types" and replies, and the live feed draws it. Text messages only - a reaction, an action or a file goes nowhere.
            const Doc: Record<string, unknown> | null = kJsonObject.ObjectOfJson(Json);
            if (Doc !== null && typeof Doc.chatId === "string" && typeof Doc.text === "string" && Doc.text.length > 0
                && Doc.react === undefined && Doc.act === undefined) {
                const Req: XMLHttpRequest = new XMLHttpRequest();
                Req.open("POST", this.Base + "/demo/reply", true);
                Req.setRequestHeader("Content-Type", "application/json");
                Req.send(JSON.stringify({ number: this.Number, chatId: Doc.chatId, text: Doc.text }));
            }
            OnDone(Name);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemoSource.Queue");
        }
    };

    /**
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public CanCheck = (): boolean => {
        return true;
    };

    /** Every demo "send" is answered sent - it went nowhere, which the demo band says. */
    public Check = (Id: string, OnState: AppSendState): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            OnState(true, true, "", false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemoSource.Check");
        }
    };

    /**
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public SendNote = (): string => {
        return "Demo mode - nothing is sent.";
    };

    /**
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Who = (): string => {
        return "demo";
    };

    /**
     * DOCUMENTED EXCEPTION: returns a literal.
     * ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw.
     */
    public Describe = (): string => {
        return "the demo archive";
    };
}

/** One read of a demo person file: answered exactly once (200 text, 404 missing, anything else a sentence). */
export class AppDemoBeside {
    private Req: XMLHttpRequest;
    private Done: AppTextDone;
    private Missing: () => void;
    private Fail: AppTextFail;
    private Answered: boolean = false;

    public constructor(Req: XMLHttpRequest, Done: AppTextDone, Missing: () => void, Fail: AppTextFail) {
        this.Req = Req;
        this.Done = Done;
        this.Missing = Missing;
        this.Fail = Fail;
    }

    public OnState = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the panel waits on this answer.
        try {
            if (this.Req.readyState !== 4 || this.Answered === true) { return; }
            this.Answered = true;
            if (this.Req.status === 200) { this.Done(this.Req.responseText); return; }
            if (this.Req.status === 404) { this.Missing(); return; }
            this.Fail("The demo could not be reached.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDemoBeside.OnState");
        }
    };
}
